import type { useSetAtom } from 'jotai/react';
import { useAtomCallback } from 'jotai/utils';
import type { Getter, Setter } from 'jotai/vanilla';
import { type DependencyList, useCallback } from 'react';

type Options = Parameters<typeof useSetAtom>[1];

/**
 * A wrapper around jotai's {@link useAtomCallback} that memoizes the callback
 * for you via {@link useCallback}, so you don't have to remember to wrap it
 * yourself.
 *
 * This is intended to eventually replace direct usage of `useAtomCallback`.
 *
 * @param callback the atom callback to memoize and register
 * @param deps the dependency array for the memoized callback
 * @param options options forwarded to `useAtomCallback`
 * @returns the stable callback returned by `useAtomCallback`
 */
export function useAtomCallbackMemoized<Result, Args extends unknown[]>(
  callback: (get: Getter, set: Setter, ...args: Args) => Result,
  deps: DependencyList,
  options?: Options,
): (...args: Args) => Result {
  // The caller supplies the dependency array, so exhaustive-deps can't verify it.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedCallback = useCallback(callback, deps);

  return useAtomCallback(memoizedCallback, options);
}
